// フォント自身が持つ埋め込み可否 (OS/2 テーブルの fsType) を読む。
//
// プロジェクト (.bpe.zip) にはフォントの原本をそのまま同梱する。サブセット化していないので、
// 受け取った側は unzip で取り出してインストールできてしまう。商用フォントの多くはこれを禁じていて、
// その意思は fsType というフィールドに機械可読な形で入っている。保存を止めはしないが、黙って通さない。
//
// woff2 は Brotli 圧縮で、展開には依存関係を増やす必要があるため 'unknown' を返す。

import { unzlibSync } from 'fflate'

export type EmbedPermission =
  /** 制限なし (インストールも可) */
  | 'installable'
  /** 埋め込み・受け渡しを禁止 */
  | 'restricted'
  /** 表示と印刷のみ (編集できる形での同梱は範囲外) */
  | 'preview-print'
  /** 編集可能な形での埋め込みを許可 */
  | 'editable'
  /** 判定できなかった (woff2 / 壊れている / OS/2 が無い) */
  | 'unknown'

export interface FontEmbedIssue {
  family: string
  filename: string
  permission: EmbedPermission
}

function tag4(b: Uint8Array, o: number): string {
  return b.length >= o + 4 ? String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!) : ''
}

/** OS/2 の先頭 10 バイトから fsType (オフセット 8、uint16) を取り出す */
function fsTypeOf(os2: Uint8Array): number | null {
  return os2.length >= 10 ? (os2[8]! << 8) | os2[9]! : null
}

function fromSfnt(b: Uint8Array, base: number): number | null {
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength)
  if (b.length < base + 12) return null
  const numTables = view.getUint16(base + 4)
  for (let i = 0; i < numTables; i++) {
    const rec = base + 12 + i * 16
    if (rec + 16 > b.length) return null
    if (tag4(b, rec) !== 'OS/2') continue
    const off = view.getUint32(rec + 8)
    if (off + 10 > b.length) return null
    return fsTypeOf(b.subarray(off, off + 10))
  }
  return null
}

function fromWoff(b: Uint8Array): number | null {
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength)
  if (b.length < 44) return null
  const numTables = view.getUint16(12)
  for (let i = 0; i < numTables; i++) {
    const rec = 44 + i * 20
    if (rec + 20 > b.length) return null
    if (tag4(b, rec) !== 'OS/2') continue
    const off = view.getUint32(rec + 4)
    const comp = view.getUint32(rec + 8)
    const orig = view.getUint32(rec + 12)
    if (off + comp > b.length) return null
    const raw = b.subarray(off, off + comp)
    try {
      return fsTypeOf(comp < orig ? unzlibSync(raw) : raw)
    } catch {
      return null
    }
  }
  return null
}

/** fsType を読む。読めなければ null */
export function readFsType(bytes: Uint8Array): number | null {
  const sig = tag4(bytes, 0)
  if (sig === 'wOFF') return fromWoff(bytes)
  if (sig === 'wOF2') return null // Brotli
  if (sig === 'ttcf') {
    if (bytes.length < 16) return null
    return fromSfnt(bytes, new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(12))
  }
  return fromSfnt(bytes, 0)
}

export function embedPermission(bytes: Uint8Array): EmbedPermission {
  const fsType = readFsType(bytes)
  if (fsType === null) return 'unknown'
  // 下位 4 ビットが許可の種類。上位は補助 (0x0100 = サブセット禁止、0x0200 = ビットマップのみ)
  if (fsType & 0x0002) return 'restricted'
  if (fsType & 0x0004) return 'preview-print'
  if (fsType & 0x0008) return 'editable'
  return 'installable'
}

/** 編集できる形でプロジェクトに同梱してよいと、フォント自身が言っているか */
export function mayEmbed(p: EmbedPermission): boolean {
  return p !== 'restricted' && p !== 'preview-print'
}

/** 同梱したフォントのうち、フォント自身が許可していないものを挙げる */
export function embedIssues(fonts: { family: string; filename: string; data: Uint8Array }[]): FontEmbedIssue[] {
  return fonts
    .map(f => ({ family: f.family, filename: f.filename, permission: embedPermission(f.data) }))
    .filter(i => !mayEmbed(i.permission))
}
